import { useContext, useEffect, useState, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Typography,
  Chip,
  alpha,
  useTheme,
  InputAdornment,
  TextField,
  Fade,
} from "@mui/material";
import {
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Group as GroupIcon,
  Domain as DomainIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useQuery } from "react-query";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";

function TenantSelection({
  userIdentifier,
  identifierType,
  onBack,
  onTenantSelected,
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { switchToTenant } = useContext(AuthContext);
  const theme = useTheme();

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [hoveredTenant, setHoveredTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Build query params for the API call
  const queryParams = {
    [identifierType]: userIdentifier,
  };

  // Fetch user tenants
  const {
    data: tenantsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["user-tenants", userIdentifier, identifierType],
    () => axios.get("/users/tenants", { params: queryParams }),
    {
      enabled: !!userIdentifier,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const tenants = tenantsResponse?.data?.data || [];

  // Filter tenants based on search query
  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;

    const query = searchQuery.toLowerCase();
    return tenants.filter((tenant) => {
      const tenantName = tenant?.tenant?.name?.toLowerCase() || "";
      const tenantId = tenant?.tenant?.id?.toLowerCase() || "";
      const origins = tenant?.tenant?.origins?.join(" ").toLowerCase() || "";

      return (
        tenantName.includes(query) ||
        tenantId.includes(query) ||
        origins.includes(query)
      );
    });
  }, [tenants, searchQuery]);

  const showSearch = tenants.length > 3;

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    setConfirmDialogOpen(true);
  };

  const handleConfirmSelection = async () => {
    if (!selectedTenant) return;

    try {
      // Switch to the selected tenant context
      const result = await switchToTenant(selectedTenant.id, false);

      if (result.success) {
        // Notify parent component about tenant selection
        if (onTenantSelected) {
          onTenantSelected(selectedTenant);
        }

        enqueueSnackbar(`Successfully switched to ${selectedTenant.name}`, {
          variant: "success",
          autoHideDuration: 2000,
        });
      }
    } catch (error) {
      console.error("Error switching tenant:", error);
      enqueueSnackbar("Failed to switch tenant. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setConfirmDialogOpen(false);
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setSelectedTenant(null);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleRetry = () => {
    refetch();
  };

  // Handle errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load tenants";

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 4000,
      });
    }
  }, [error, enqueueSnackbar]);

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            background: theme => theme.palette.mode === 'light'
              ? "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)"
              : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            border: `1px solid ${theme.palette.divider}`,
            mx: { xs: 1, sm: 0 },
          }}
        >
          {/* Header Skeleton */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Skeleton
              variant="rectangular"
              width={{ xs: 45, sm: 60 }}
              height={{ xs: 35, sm: 45 }}
              sx={{ mr: 2, borderRadius: 1 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                variant="text"
                width="40%"
                height={{ xs: 30, sm: 40 }}
              />
              <Skeleton variant="text" width="60%" />
            </Box>
          </Box>

          {/* Loading skeleton for tenant cards */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            Loading your available tenants...
          </Typography>

          {[1, 2, 3].map((item) => (
            <Card
              key={item}
              sx={{
                mb: 2,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Skeleton
                  variant="circular"
                  width={{ xs: 50, sm: 60 }}
                  height={{ xs: 50, sm: 60 }}
                  sx={{ mr: { xs: 2, sm: 3 } }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={{ xs: 24, sm: 28 }}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={{ xs: 18, sm: 20 }}
                  />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={16}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Paper>
      </Container>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            background: theme => theme.palette.mode === 'light'
              ? "linear-gradient(145deg, #fef2f2 0%, #ffffff 100%)"
              : alpha(theme.palette.error.main, 0.05),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            mx: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "space-between", sm: "flex-start" },
                mb: { xs: 1, sm: 0 },
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Back
              </Button>
              <Avatar
                sx={{
                  width: { xs: 45, sm: 50, md: 60 },
                  height: { xs: 35, sm: 38, md: 45 },
                  borderRadius: 0,
                  mr: { xs: 0, sm: 2 },
                  order: { xs: -1, sm: 0 },
                }}
                src={logo}
                alt="Logo"
              />
            </Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Select Tenant
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", py: 4 }}>
            <ErrorIcon
              sx={{
                fontSize: { xs: 48, sm: 64 },
                color: "error.main",
                mb: 2,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="h6"
              color="error"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              Unable to Load Tenants
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 3,
                maxWidth: 400,
                mx: "auto",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              We encountered an issue while loading your available tenants. This
              might be due to network issues or server problems.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "center", sm: "flex-end" },
                flexWrap: "wrap",
                flexDirection: { xs: "column-reverse", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                onClick={handleRetry}
                startIcon={<SecurityIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: "primary.main",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  // No tenants found
  if (tenants.length === 0 && !isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            background: theme => theme.palette.mode === 'light'
              ? "linear-gradient(145deg, #f0f9ff 0%, #ffffff 100%)"
              : alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mx: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: 2, sm: 0 },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "space-between", sm: "flex-start" },
                mb: { xs: 1, sm: 0 },
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Back
              </Button>
              <Avatar
                sx={{
                  width: { xs: 45, sm: 50, md: 60 },
                  height: { xs: 35, sm: 38, md: 45 },
                  borderRadius: 0,
                  mr: { xs: 0, sm: 2 },
                  order: { xs: -1, sm: 0 },
                }}
                src={logo}
                alt="Logo"
              />
            </Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Select Tenant
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", py: 4 }}>
            <DomainIcon
              sx={{
                fontSize: { xs: 48, sm: 64 },
                color: "primary.main",
                mb: 2,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              No Tenants Available
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 3,
                maxWidth: 400,
                mx: "auto",
                lineHeight: 1.6,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              No tenants are currently associated with this {identifierType}.
              You can register a new tenant or contact your administrator for
              access.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "center", sm: "flex-end" },
                flexWrap: "wrap",
                flexDirection: { xs: "column-reverse", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Go Back
              </Button>
              {/* <Button
                variant="contained"
                onClick={() => navigate("/tenant-registration")}
                startIcon={<BusinessIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: "primary.main",
                  boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Register New Tenant
              </Button> */}
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: 2, md: 3 },
          background: theme => theme.palette.mode === 'light'
            ? "linear-gradient(145deg, #fafbfc 0%, #ffffff 100%)"
            : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          mx: { xs: 1, sm: 0 },
          display: "flex",
          flexDirection: "column",
          maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 64px)", md: "calc(100vh - 96px)" },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: { xs: 0.5, sm: 4 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-start" },
              mb: { xs: 1, sm: 0 },
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                mr: { xs: 0, sm: 2 },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                minWidth: "auto",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Back
            </Button>
            <Avatar
              sx={{
                width: { xs: 45, sm: 50, md: 60 },
                height: { xs: 35, sm: 38, md: 45 },
                borderRadius: 0,
                mr: { xs: 0, sm: 2 },
                order: { xs: -1, sm: 0 },
              }}
              src={logo}
              alt="Logo"
            />
          </Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
              background: "primary.main",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Select Tenant
          </Typography>
        </Box>

        {/* Introduction Text */}
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: { xs: 2, sm: 3 },
            textAlign: "center",
            lineHeight: 1.6,
            fontSize: { xs: "0.875rem", sm: "0.95rem" },
          }}
        >
          Choose the workspace you want to access. You have access to{" "}
          <strong>{tenants.length}</strong> tenant
          {tenants.length !== 1 ? "s" : ""}.
        </Typography>

        {/* Search Field - Show when there are more than 3 tenants */}
        {showSearch && (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tenants by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: "text.secondary", fontSize: 20 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => setSearchQuery("")}
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                  "&.Mui-focused": {
                    backgroundColor: "background.paper",
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                  },
                },
              }}
            />
          </Box>
        )}

        {/* Tenant List */}
        {filteredTenants.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 2,
            }}
          >
            <SearchIcon
              sx={{
                fontSize: { xs: 48, sm: 60 },
                color: "text.disabled",
                mb: 2,
                opacity: 0.5,
              }}
            />
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                fontWeight: 500,
              }}
            >
              No tenants found
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
            >
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              pr: { xs: 0.5, sm: 1 },
              mb: { xs: 2, sm: 3 },
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 3,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.3),
                borderRadius: 3,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.5),
                },
              },
            }}
          >
            <List sx={{ p: 0 }}>
              {filteredTenants.map((tenant, index) => (
                <Fade
                  in={true}
                  timeout={300 + index * 50}
                  key={tenant?.tenant?.id}
                >
                  <Card
                    sx={{
                      mb: { xs: 1.5, sm: 2 },
                      borderRadius: 2,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform:
                        hoveredTenant === tenant?.tenant?.id
                          ? { xs: "none", sm: "translateY(-4px) scale(1.01)" }
                          : "none",
                      boxShadow: theme => hoveredTenant === tenant?.tenant?.id
                        ? theme.palette.mode === 'light'
                          ? "0px 8px 24px rgba(0, 0, 0, 0.12)"
                          : `0px 8px 24px ${alpha(theme.palette.common.black, 0.4)}`
                        : theme.palette.mode === 'light'
                          ? "0px 2px 8px rgba(0, 0, 0, 0.06)"
                          : "none",
                      border: `2px solid ${alpha(
                        theme.palette.primary.main,
                        hoveredTenant === tenant?.tenant?.id ? 0.3 : 0.08
                      )}`,
                      overflow: "hidden",
                      position: "relative",
                      background:
                        hoveredTenant === tenant?.tenant?.id
                          ? "linear-gradient(145deg, #fafbfc 0%, #ffffff 100%)"
                          : "#ffffff",
                      cursor: "pointer",
                      "&::before":
                        hoveredTenant === tenant?.tenant?.id
                          ? {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main
                              }, ${alpha(theme.palette.primary.main, 0.6)})`,
                          }
                          : {},
                    }}
                    onMouseEnter={() => setHoveredTenant(tenant?.tenant?.id)}
                    onMouseLeave={() => setHoveredTenant(null)}
                  >
                    <ListItemButton
                      onClick={() => handleTenantSelect(tenant?.tenant)}
                      sx={{
                        p: { xs: 2, sm: 2.5, md: 3 },
                        borderRadius: 2,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={tenant?.tenant?.logo}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            width: { xs: 56, sm: 64 },
                            height: { xs: 56, sm: 64 },
                            mr: { xs: 2, sm: 2.5 },
                            boxShadow:
                              hoveredTenant === tenant?.tenant?.id
                                ? "0px 4px 12px rgba(0, 0, 0, 0.15)"
                                : "0px 2px 6px rgba(0, 0, 0, 0.1)",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform:
                              hoveredTenant === tenant?.tenant?.id
                                ? { xs: "none", sm: "scale(1.08) rotate(2deg)" }
                                : "scale(1)",
                            border: `2px solid ${alpha(
                              theme.palette.primary.main,
                              hoveredTenant === tenant?.tenant?.id ? 0.3 : 0.1
                            )}`,
                          }}
                        >
                          <BusinessIcon
                            sx={{
                              color: theme.palette.primary.main,
                              fontSize: { xs: 28, sm: 32 },
                            }}
                          />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="div"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: { xs: "1.05rem", sm: "1.15rem" },
                                lineHeight: 1.3,
                              }}
                            >
                              {tenant?.tenant?.name}
                            </Typography>
                            {index === 0 && (
                              <Chip
                                label="Recent"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  color: theme.palette.primary.main,
                                  border: `1px solid ${alpha(
                                    theme.palette.primary.main,
                                    0.3
                                  )}`,
                                  "& .MuiChip-label": {
                                    px: 1,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            {tenant?.tenant?.origins &&
                              tenant?.tenant?.origins.length > 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontSize: { xs: "0.75rem", sm: "0.78rem" },
                                    opacity: 0.8,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {tenant?.tenant?.origins.join(", ")}
                                </Typography>
                              )}
                          </Box>
                        }
                      />
                      <Box
                        sx={{
                          opacity:
                            hoveredTenant === tenant?.tenant?.id ? 1 : 0.3,
                          transition: "all 0.3s ease",
                          color: "primary.main",
                          transform:
                            hoveredTenant === tenant?.tenant?.id
                              ? "scale(1.2)"
                              : "scale(1)",
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 28 }} />
                      </Box>
                    </ListItemButton>
                  </Card>
                </Fade>
              ))}
            </List>
          </Box>
        )}

        {/* Additional Actions */}
        {/* <Box
          sx={{
            textAlign: "center",
            p: { xs: 2.5, sm: 3 },
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.03
            )} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
            border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
            transition: "all 0.3s ease",
            "&:hover": {
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.05
              )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              borderColor: alpha(theme.palette.primary.main, 0.35),
            },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
              fontWeight: 500,
            }}
          >
            Need access to another workspace?
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/tenant-registration")}
            startIcon={<BusinessIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
              px: { xs: 2.5, sm: 3 },
              py: { xs: 1, sm: 1.2 },
              borderWidth: 1.5,
              borderColor: alpha(theme.palette.primary.main, 0.4),
              color: theme.palette.primary.main,
              "&:hover": {
                borderWidth: 1.5,
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: "translateY(-2px)",
                boxShadow: `0 4px 12px ${alpha(
                  theme.palette.primary.main,
                  0.2
                )}`,
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Register New Tenant
          </Button>
        </Box> */}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
            m: { xs: 2, sm: 3 },
            width: { xs: "calc(100% - 32px)", sm: "450px" },
            maxWidth: "450px",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            textAlign: "center",
            fontSize: { xs: "1.15rem", sm: "1.25rem" },
            px: 3,
            pt: 3,
            pb: 1,
            color: "text.primary",
            background: theme => theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)"
              : "transparent",
          }}
        >
          Confirm Workspace Selection
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2, pt: 3, mt: 2 }}>
          <Box sx={{ textAlign: "center" }}>
            <Avatar
              src={selectedTenant?.logo}
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <BusinessIcon
                sx={{ fontSize: 40, color: theme.palette.primary.main }}
              />
            </Avatar>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: "1.2rem",
                color: "text.primary",
                mb: 1,
              }}
            >
              {selectedTenant?.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.6,
                px: 1,
              }}
            >
              You are about to access the{" "}
              <strong>{selectedTenant?.name}</strong> workspace.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: { md: "flex", xs: "none" },
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderColor: "divider",
                color: "text.secondary",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                ":hover": {
                  borderColor: "text.primary",
                  bgcolor: "action.hover",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={handleConfirmSelection}
              autoFocus
              sx={{
                py: 1.5,
                bgcolor: "primary.main",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                "&:hover": {
                  transform: { xs: "none", sm: "translateY(-1px)" },
                  boxShadow: theme => `0 4px 8px 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
              }}
            >
              Continue
            </Button>
          </Box>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={handleConfirmSelection}
              autoFocus
              sx={{
                py: 1.5,
                bgcolor: "primary.main",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                boxShadow: theme => `0 3px 5px 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              Continue
            </Button>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderColor: "divider",
                color: "text.secondary",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 15,
                ":hover": {
                  borderColor: "text.primary",
                  bgcolor: "action.hover",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TenantSelection;
